// DataSourcesPanel: transparency panel showing the intelligence pipeline + what was fetched.
// Includes: how it works diagram, pipeline stats, source breakdown, recent news samples,
// per-provider collection status.

import React, { useState } from 'react';
import type { Lang } from '../lib/i18n';
import type { DataSource, PipelineStats, Provider, ReviewSignal } from '../lib/api';

interface NewsItem {
  id: number;
  provider_id: string;
  headline: string | null;
  category: string | null;
  summary: string | null;
  source_name: string | null;
  source_url: string | null;
  date: string | null;
  significance: string | null;
}

interface DataSourcesPanelProps {
  dataSources: DataSource[];
  pipelineStats: PipelineStats | null;
  news?: NewsItem[];
  providers?: Provider[];
  signals?: ReviewSignal[];
  lang: Lang;
}

const TYPE_LABELS: Record<string, { tr: string; en: string }> = {
  SOCIAL: { tr: 'Sosyal Medya', en: 'Social Media' },
  REVIEWS: { tr: 'Kullanıcı Yorumları', en: 'User Reviews' },
  NEWS: { tr: 'Haber', en: 'News' },
  EDITORIAL: { tr: 'Editöryel', en: 'Editorial' },
  WEB: { tr: 'Web', en: 'Web' },
  SOCIAL_MEDIA: { tr: 'Sosyal Medya', en: 'Social Media' },
};

function typeLabel(type: string, lang: Lang): string {
  const entry = TYPE_LABELS[type.toUpperCase()];
  return entry ? entry[lang] : type;
}

interface SampleQuote {
  text: string;
  source: string;
  url?: string;
  sentiment?: 'positive' | 'neutral' | 'negative';
  date?: string;
}

interface DrilldownItem {
  text: string;
  url?: string;
  sentiment?: 'positive' | 'neutral' | 'negative';
  date?: string;
  providerId: string;
  headline?: string;
  isNews?: boolean;
  sourceName?: string;
}

function parseQuotes(raw: string | null | undefined): SampleQuote[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((q) => q && typeof q.text === 'string');
  } catch {
    return [];
  }
}

// Match a data-source row to its underlying records. For review-type sources
// (Trustpilot/Reddit/YouTube/etc.) we drill into reviews_signals.sample_quotes.
// For news/web/editorial-type sources we drill into provider_news items.
function drilldownForSource(
  srcName: string,
  srcType: string,
  signals: ReviewSignal[],
  news: NewsItem[]
): DrilldownItem[] {
  const needle = srcName.toLowerCase();
  const type = srcType.toUpperCase();
  const out: DrilldownItem[] = [];

  const isNewsLike = type === 'NEWS' || type === 'EDITORIAL' || type === 'WEB';

  if (isNewsLike) {
    for (const n of news) {
      const s = (n.source_name ?? '').toLowerCase();
      if (!s) continue;
      const matches =
        s.includes(needle) ||
        needle.includes(s) ||
        (needle.includes('news') && /reuters|bloomberg|techcrunch|cnbc|forbes|wsj|ft\.|telecompaper|globenewswire|business insider|times of india|benzinga/.test(s)) ||
        (needle.includes('web') && n.source_url?.startsWith('http'));
      if (!matches) continue;
      out.push({
        text: n.summary ?? n.headline ?? '',
        headline: n.headline ?? undefined,
        url: n.source_url ?? undefined,
        date: n.date ?? undefined,
        providerId: n.provider_id,
        sourceName: n.source_name ?? undefined,
        isNews: true,
      });
    }
  } else {
    // Reviews / social: pull quotes from reviews_signals
    for (const s of signals) {
      if (s.source.toLowerCase() !== needle) continue;
      for (const q of parseQuotes(s.sample_quotes)) {
        out.push({
          text: q.text,
          url: q.url,
          sentiment: q.sentiment,
          date: q.date,
          providerId: s.provider_id,
        });
      }
    }
  }

  return out;
}

export function DataSourcesPanel({
  dataSources,
  pipelineStats,
  news = [],
  providers = [],
  signals = [],
  lang,
}: DataSourcesPanelProps) {
  const [newsExpanded, setNewsExpanded] = useState(false);
  const [statusExpanded, setStatusExpanded] = useState(false);
  const [openSourceIdx, setOpenSourceIdx] = useState<number | null>(null);

  const activeSources = dataSources.filter((s) => s.success && s.records_fetched > 0);

  if (activeSources.length === 0 && !pipelineStats) {
    return null;
  }

  // Per-provider signal counts (collection status)
  const signalsByProvider = new Map<string, number>();
  for (const s of signals) {
    signalsByProvider.set(s.provider_id, (signalsByProvider.get(s.provider_id) ?? 0) + (s.mention_count ?? 0));
  }
  const newsByProvider = new Map<string, number>();
  for (const n of news) {
    newsByProvider.set(n.provider_id, (newsByProvider.get(n.provider_id) ?? 0) + 1);
  }

  // Top news
  const topNews = news
    .filter((n) => n.headline)
    .sort((a, b) => {
      const sigOrder: Record<string, number> = { high: 3, medium: 2, low: 1 };
      return (sigOrder[b.significance ?? 'low'] ?? 0) - (sigOrder[a.significance ?? 'low'] ?? 0);
    })
    .slice(0, 6);

  return (
    <section>
      <h2 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3">
        {lang === 'tr' ? 'VERİ KAYNAKLARI VE ZEKÂ KATMANI' : 'DATA SOURCES & INTELLIGENCE LAYER'}
      </h2>

      <div className="card p-4 space-y-5">
        {/* Intelligence layer explanation */}
        <div className="border-l-2 border-emerald-500/40 pl-3 py-1">
          <div className="text-xs font-medium text-neutral-800 mb-1">
            {lang === 'tr' ? 'Pipeline nasıl çalışıyor?' : 'How the pipeline works'}
          </div>
          <div className="text-xs text-neutral-600 leading-relaxed mb-3">
            {lang === 'tr'
              ? 'Her Pazartesi 07:00 UTC\'de Cloudflare Workers cron tetiklenir. 28 sağlayıcı 4\'lü gruplar halinde paralel işlenir. Her sağlayıcı için Perplexity Sonar üzerinden 3 paralel canlı arama çalıştırılır. Tüm ham veri toplandıktan sonra tek bir sentez Sonar çağrısı temaları, aksiyon önerilerini ve haftalık özeti üretir. Toplam: 28 × 3 + 1 = 85 Sonar çağrısı, ~$1/hafta.'
              : 'Every Monday at 07:00 UTC, the Cloudflare Workers cron fires. 28 providers are processed in batches of 4 in parallel. For each provider, three parallel Sonar live-search queries run. Once all raw data is collected, a single synthesis Sonar call produces themes, action recommendations and the weekly summary. Total: 28 × 3 + 1 = 85 Sonar calls, ~$1/week.'}
          </div>

          {/* Mini pipeline diagram */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 text-[11px]">
            <PipelineStep
              num="1"
              title={lang === 'tr' ? 'Fiyat planları' : 'Pricing plans'}
              desc={lang === 'tr' ? 'plan adı · bölge · GB · gün · USD · kaynak URL' : 'plan name · region · GB · days · USD · source URL'}
              tag="fetchPlansSonar"
            />
            <PipelineStep
              num="2"
              title={lang === 'tr' ? 'Duygu & geri bildirim' : 'Sentiment & feedback'}
              desc={lang === 'tr' ? 'kullanıcı alıntıları · pozitif/nötr/negatif · kaynak başına sayım' : 'user quotes · positive/neutral/negative · per-source counts'}
              tag="fetchSentimentSonar"
            />
            <PipelineStep
              num="3"
              title={lang === 'tr' ? 'Haber & rekabet' : 'News & competitive'}
              desc={lang === 'tr' ? 'lansman · ortaklık · fonlama · regülasyon' : 'launches · partnerships · funding · regulation'}
              tag="fetchNewsSonar"
            />
          </div>
          <div className="mt-2 text-[11px] text-neutral-500 flex items-center gap-1.5">
            <span>↓</span>
            <span className="font-medium text-neutral-700">{lang === 'tr' ? 'Sentez' : 'Synthesis'}</span>
            <span className="text-neutral-500">→</span>
            <span>{lang === 'tr' ? 'Türkçe temalar · aksiyonlar · checklist' : 'Turkish themes · actions · checklist'}</span>
            <span className="text-neutral-300 text-[10px] ml-1">(llmSynthesize)</span>
          </div>
        </div>

        {/* Pipeline stats */}
        {pipelineStats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
            <Stat
              label={lang === 'tr' ? 'Taranan oyuncu' : 'Providers scanned'}
              value={pipelineStats.providers_fetched}
              sub={pipelineStats.providers_failed > 0
                ? `${pipelineStats.providers_failed} ${lang === 'tr' ? 'başarısız' : 'failed'}`
                : (lang === 'tr' ? 'hepsi başarılı' : 'all succeeded')}
            />
            <Stat
              label={lang === 'tr' ? 'Plan toplandı' : 'Plans collected'}
              value={pipelineStats.plans_inserted}
              sub={`${pipelineStats.plans_verified} ${lang === 'tr' ? 'doğrulandı' : 'verified'}`}
            />
            <Stat
              label={lang === 'tr' ? 'Sinyal kaydı' : 'Signal rows'}
              value={pipelineStats.signals_inserted}
            />
            <Stat
              label={lang === 'tr' ? 'Haber kaydı' : 'News items'}
              value={news.length}
            />
            <Stat
              label={lang === 'tr' ? 'Çalışma süresi' : 'Run duration'}
              value={`${(pipelineStats.duration_ms / 1000).toFixed(1)}s`}
              sub={lang === 'tr' ? '85 Sonar çağrısı' : '85 Sonar calls'}
            />
          </div>
        )}

        {/* Source list — each row expandable to show the actual captured URLs */}
        {activeSources.length > 0 && (
          <div>
            <div className="text-xs font-medium text-neutral-600 mb-2">
              {lang === 'tr' ? 'Bu hafta veri toplanan sinyal kaynakları' : 'Signal sources with data this week'}
            </div>
            <div className="grid gap-1.5">
              {activeSources.map((src, i) => {
                const items = drilldownForSource(src.name, src.type, signals, news);
                const expandable = items.length > 0;
                const isOpen = openSourceIdx === i;
                const providerNameOf = (id: string) => providers.find((p) => p.id === id)?.name ?? id;
                return (
                  <div key={i} className="rounded border border-neutral-200">
                    <button
                      type="button"
                      disabled={!expandable}
                      onClick={() => expandable && setOpenSourceIdx(isOpen ? null : i)}
                      className={`w-full flex items-center justify-between text-xs py-1.5 px-2 text-left ${expandable ? 'hover:bg-neutral-100/40 cursor-pointer' : 'cursor-default'}`}
                      aria-expanded={isOpen}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500/60 shrink-0" />
                        <span className="text-neutral-800 truncate">{src.name}</span>
                        <span className="text-neutral-500 text-[10px] uppercase">
                          {typeLabel(src.type, lang)}
                        </span>
                        {expandable && (
                          <span className="text-neutral-500 text-[10px]">{isOpen ? '▲' : '▼'}</span>
                        )}
                      </div>
                      <span className="tabular-nums text-neutral-600 shrink-0">
                        {src.records_fetched.toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US')} {lang === 'tr' ? 'kayıt' : 'records'}
                      </span>
                    </button>
                    {isOpen && items.length > 0 && (
                      <ul className="px-2 pb-2 space-y-1.5 border-t border-neutral-200/60 pt-1.5">
                        {items.slice(0, 8).map((it, idx) => (
                          <li key={idx} className="text-[11px] leading-snug">
                            <div className="flex items-start gap-1.5">
                              <span
                                className={`w-1 h-1 rounded-full mt-1.5 shrink-0 ${
                                  it.sentiment === 'positive'
                                    ? 'bg-emerald-500/70'
                                    : it.sentiment === 'negative'
                                      ? 'bg-rose-500/70'
                                      : it.sentiment === 'neutral'
                                        ? 'bg-neutral-500/60'
                                        : 'bg-neutral-300'
                                }`}
                              />
                              <div className="flex-1 min-w-0">
                                {it.isNews ? (
                                  <div className="text-neutral-700">{it.headline ?? it.text}</div>
                                ) : (
                                  <div className="text-neutral-700">“{it.text}”</div>
                                )}
                                <div className="text-[10px] text-neutral-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                                  <span>{providerNameOf(it.providerId)}</span>
                                  {it.date && <span>· {it.date}</span>}
                                  {it.url && it.url.startsWith('http') && (
                                    <a
                                      href={it.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-emerald-600/80 hover:text-emerald-600 underline decoration-dotted"
                                    >
                                      {lang === 'tr' ? 'kaynak →' : 'source →'}
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                        {items.length > 8 && (
                          <li className="text-[10px] text-neutral-500">
                            +{items.length - 8} {lang === 'tr' ? 'daha' : 'more'}
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent news samples */}
        {news.length > 0 && (
          <div>
            <button
              onClick={() => setNewsExpanded(!newsExpanded)}
              className="text-xs font-medium text-neutral-600 hover:text-neutral-800 transition-colors mb-2 flex items-center gap-1.5"
            >
              <span>
                {lang === 'tr'
                  ? `Bu hafta yakalanan haber & rekabet hareketleri (${news.length})`
                  : `News & competitive moves captured this week (${news.length})`}
              </span>
              <span className="text-neutral-500">{newsExpanded ? '▲' : '▼'}</span>
            </button>
            {newsExpanded && (
              <div className="space-y-1.5">
                {topNews.map((n) => (
                  <div key={n.id} className="text-xs py-1.5 px-2 rounded border border-neutral-200">
                    <div className="flex items-start gap-2">
                      {n.significance && (
                        <span
                          className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 mt-0.5 ${
                            n.significance === 'high'
                              ? 'bg-rose-500/10 text-rose-600'
                              : n.significance === 'medium'
                                ? 'bg-amber-500/10 text-amber-600'
                                : 'bg-neutral-200 text-neutral-500'
                          }`}
                        >
                          {n.significance === 'high'
                            ? (lang === 'tr' ? 'YÜKSEK' : 'HIGH')
                            : n.significance === 'medium'
                              ? (lang === 'tr' ? 'ORTA' : 'MED')
                              : (lang === 'tr' ? 'DÜŞÜK' : 'LOW')}
                        </span>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-neutral-800 leading-snug">{n.headline}</div>
                        {n.summary && (
                          <div className="text-neutral-500 mt-0.5 leading-relaxed">{n.summary}</div>
                        )}
                        <div className="text-[10px] text-neutral-500 mt-1 flex items-center gap-2">
                          {n.source_name && <span>{n.source_name}</span>}
                          {n.date && <span>{n.date}</span>}
                          {n.source_url && n.source_url.startsWith('http') && (
                            <a
                              href={n.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-emerald-600/70 hover:text-emerald-600 underline decoration-dotted"
                            >
                              {lang === 'tr' ? 'kaynak' : 'source'}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Per-provider collection status */}
        {providers.length > 0 && (
          <div>
            <button
              onClick={() => setStatusExpanded(!statusExpanded)}
              className="text-xs font-medium text-neutral-600 hover:text-neutral-800 transition-colors mb-2 flex items-center gap-1.5"
            >
              <span>
                {lang === 'tr'
                  ? `Sağlayıcı bazlı toplama durumu (${providers.length})`
                  : `Per-provider collection status (${providers.length})`}
              </span>
              <span className="text-neutral-500">{statusExpanded ? '▲' : '▼'}</span>
            </button>
            {statusExpanded && (
              <div className="overflow-x-auto">
                <table className="data-table min-w-[500px]">
                  <thead>
                    <tr>
                      <th>{lang === 'tr' ? 'Sağlayıcı' : 'Provider'}</th>
                      <th className="text-right">{lang === 'tr' ? 'Bahsedilme' : 'Mentions'}</th>
                      <th className="text-right">{lang === 'tr' ? 'Haber' : 'News'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {providers
                      .slice()
                      .sort((a, b) => (signalsByProvider.get(b.id) ?? 0) - (signalsByProvider.get(a.id) ?? 0))
                      .map((p) => {
                        const m = signalsByProvider.get(p.id) ?? 0;
                        const n = newsByProvider.get(p.id) ?? 0;
                        return (
                          <tr key={p.id}>
                            <td className="text-xs text-neutral-700">{p.name}</td>
                            <td className="text-right tabular-nums text-xs">
                              <span className={m > 0 ? 'text-neutral-700' : 'text-neutral-500'}>{m}</span>
                            </td>
                            <td className="text-right tabular-nums text-xs">
                              <span className={n > 0 ? 'text-neutral-700' : 'text-neutral-500'}>{n}</span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <div>
      <div className="text-lg font-semibold text-neutral-900 tabular-nums">{value}</div>
      <div className="text-[10px] text-neutral-500 uppercase tracking-wider">{label}</div>
      {sub && <div className="text-[10px] text-neutral-500">{sub}</div>}
    </div>
  );
}

function PipelineStep({ num, title, desc, tag }: { num: string; title: string; desc: string; tag: string }) {
  return (
    <div className="border border-neutral-200 rounded p-2">
      <div className="flex items-center gap-1.5 mb-1">
        <span className="w-4 h-4 rounded-full bg-emerald-500/15 text-emerald-600 text-[10px] flex items-center justify-center font-medium">
          {num}
        </span>
        <span className="text-neutral-800 font-medium">{title}</span>
      </div>
      <div className="text-neutral-500 leading-relaxed">{desc}</div>
      <div className="text-[9px] text-neutral-300 mt-1 font-mono">{tag}</div>
    </div>
  );
}
